import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';

const BOT_URL       = process.env.REACT_APP_BOT_URL || 'http://localhost:8080';
const getAdminPassword = () => sessionStorage.getItem('eco_admin_pw') || 'Ecoholic@2025';

const getSafeUrl = (url) => {
  if (!url) return '#';
  const trimmed = url.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return '#';
};

export default function LocationsManager() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Modals state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLoc, setEditingLoc] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    locationId: '', locationName: '', address: '', mapLocation: '',
    priority: '', clusterManager: '', clusterPhone: '', outletId: '', active: true,
    location: '', state: '', city: '', storeManagerContact: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchLocations(); }, []);

  const fetchLocations = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'locations'));
      const locList = snap.docs.map(d => ({ ...d.data(), id: d.id }));
      
      // Sort: ES1, ES2... properly instead of alphabetical
      locList.sort((a, b) => {
        const numA = parseInt((a.locationId || '').replace(/\\D/g, '') || 0);
        const numB = parseInt((b.locationId || '').replace(/\\D/g, '') || 0);
        return numA - numB;
      });
      
      setLocations(locList);
    } catch (err) {
      console.error(err);
      alert("Failed to fetch locations: " + err.message);
    }
    setLoading(false);
  };

  const handleEdit = (loc) => {
    setEditingLoc(loc);
    setFormData({
      locationId: loc.locationId || '',
      locationName: loc.locationName || '',
      address: loc.address || '',
      mapLocation: loc.mapLocation || '',
      priority: loc.priority || '',
      clusterManager: loc.clusterManager || '',
      clusterPhone: loc.clusterPhone || '',
      outletId: loc.outletId || '',
      active: loc.active !== false,
      location: loc.location || '',
      state: loc.state || '',
      city: loc.city || '',
      storeManagerContact: loc.storeManagerContact || loc.clusterPhone || ''
    });
    setIsModalOpen(true);
  };

  const handleAdd = () => {
    setEditingLoc(null);
    setFormData({
      locationId: '',
      locationName: '',
      address: '',
      mapLocation: '',
      priority: '',
      clusterManager: '',
      clusterPhone: '',
      outletId: '',
      active: true,
      location: '',
      state: '',
      city: '',
      storeManagerContact: ''
    });
    setIsModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.locationId || !formData.locationName) return alert("Location ID and Name required.");
    setSaving(true);
    try {
      const locId = formData.locationId.toUpperCase().trim();
      await setDoc(doc(db, 'locations', locId), {
        ...formData,
        locationId: locId,
        updatedAt: new Date()
      }, { merge: true });
      
      setIsModalOpen(false);
      fetchLocations();
    } catch (err) {
      console.error(err);
      alert("Save failed: " + err.message);
    }
    setSaving(false);
  };

  const handleDelete = async (locId) => {
    if (!window.confirm(`Are you sure you want to delete location ${locId}? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${BOT_URL}/api/location/${locId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          adminPassword: getAdminPassword()
        })
      });
      const data = await res.json();
      if (data.success) {
        alert("Location deleted successfully.");
        fetchLocations();
      } else {
        if (res.status === 401) {
          sessionStorage.removeItem('eco_admin');
          sessionStorage.removeItem('eco_admin_pw');
          window.location.reload();
          return;
        }
        alert("❌ Error: " + (data.error || "Server error"));
      }
    } catch (err) {
      alert("❌ Error: Network error or bot server offline.");
    }
  };

  const filtered = locations.filter(l => 
    (l.locationId || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (l.locationName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (l.address || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#f8fafc' }}>
          📍 Locations Manager
        </h2>
        <button onClick={handleAdd} style={{
          background: 'linear-gradient(135deg,#38bdf8,#0284c7)',
          border: 'none', borderRadius: 10, padding: '10px 20px',
          color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer',
        }}>
          ➕ Nayi Location Add Karo
        </button>
      </div>

      <div style={{ background: 'rgba(30, 41, 59, 0.7)', padding: '16px 20px', borderRadius: 12, marginBottom: 24, display: 'flex', gap: 16 }}>
        <input 
          type="text" 
          placeholder="🔍 Search locations, ID or address..." 
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={{ flex: 1, padding: '12px 16px', borderRadius: 8, border: '1px solid #334155', background: '#0f172a', color: '#f8fafc' }}
        />
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading locations data...</div>
      ) : (
        <div style={{ background: 'rgba(30, 41, 59, 0.7)', borderRadius: 12, border: '1px solid #334155', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: 1000 }}>
            <thead>
              <tr style={{ background: 'rgba(0,0,0,0.2)', borderBottom: '1px solid #334155' }}>
                {['ID', 'Name', 'Outlet ID', 'Location', 'City/State', 'Priority', 'Address', 'Map Link', 'Manager Contact', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '16px', color: '#94a3b8', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(l => (
                <tr key={l.id} style={{ borderBottom: '1px solid #334155', transition: 'background 0.2s', ':hover': { background: 'rgba(255,255,255,0.02)' }}}>
                  <td style={{ padding: '16px', fontWeight: 600, color: '#38bdf8' }}>{l.locationId}</td>
                  <td style={{ padding: '16px', color: '#f8fafc', fontWeight: 500 }}>{l.locationName}</td>
                  <td style={{ padding: '16px', color: '#cbd5e1' }}>{l.outletId || '—'}</td>
                  <td style={{ padding: '16px', color: '#cbd5e1' }}>{l.location || '—'}</td>
                  <td style={{ padding: '16px', color: '#cbd5e1' }}>
                    {l.city || l.state ? `${l.city || '—'}, ${l.state || '—'}` : '—'}
                  </td>
                  <td style={{ padding: '16px' }}>
                    <span style={{ 
                      padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                      background: l.priority ? 'rgba(245, 158, 11, 0.15)' : 'rgba(255,255,255,0.05)',
                      color: l.priority ? '#fcd34d' : '#94a3b8' 
                    }}>
                      {l.priority || 'N/A'}
                    </span>
                  </td>
                  <td style={{ padding: '16px', color: '#94a3b8', fontSize: 13, maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={l.address}>
                    {l.address || '—'}
                  </td>
                  <td style={{ padding: '16px' }}>
                    {l.mapLocation ? (
                      <a href={getSafeUrl(l.mapLocation)} target="_blank" rel="noreferrer" style={{ 
                        color: '#34d399', textDecoration: 'none', background: 'rgba(52, 211, 153, 0.1)', 
                        padding: '6px 12px', borderRadius: 6, fontSize: 13, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 6 
                      }}>
                        🗺️ View Map
                      </a>
                    ) : '—'}
                  </td>
                  <td style={{ padding: '16px', color: '#cbd5e1', fontSize: 13 }}>
                    {l.storeManagerContact || l.clusterPhone || '—'}
                  </td>
                  <td style={{ padding: '16px' }}>
                    <span style={{ 
                      padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                      background: l.active ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                      color: l.active ? '#34d399' : '#f87171' 
                    }}>
                      {l.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button 
                        onClick={() => handleEdit(l)}
                        style={{ 
                          background: 'rgba(56, 189, 248, 0.1)', color: '#38bdf8', border: '1px solid rgba(56, 189, 248, 0.3)',
                          padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13
                        }}>
                        ✏️ Edit
                      </button>
                      <button 
                        onClick={() => handleDelete(l.locationId)}
                        style={{ 
                          background: 'rgba(239, 68, 68, 0.1)', color: '#f87171', border: '1px solid rgba(239, 68, 68, 0.3)',
                          padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13
                        }}>
                        🗑️ Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Modal */}
      {isModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#1e293b', width: 600, borderRadius: 16, padding: 30, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ margin: '0 0 20px', color: '#f8fafc', fontSize: 20 }}>
              {editingLoc ? 'Edit Location: ' + editingLoc.locationId : 'Add Location'}
            </h3>
            
            <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'flex', gap: 16 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: 6, color: '#94a3b8', fontSize: 13 }}>Location ID (e.g., ES3)</label>
                  <input type="text" value={formData.locationId} readOnly={!!editingLoc} onChange={e => setFormData({...formData, locationId: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #334155', background: editingLoc ? '#0f172a' : '#1e293b', color: '#f8fafc' }} required />
                </div>
                <div style={{ flex: 2 }}>
                  <label style={{ display: 'block', marginBottom: 6, color: '#94a3b8', fontSize: 13 }}>Location Name</label>
                  <input type="text" value={formData.locationName} onChange={e => setFormData({...formData, locationName: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #334155', background: '#1e293b', color: '#f8fafc' }} required />
                </div>
              </div>

              <div style={{ display: 'flex', gap: 16 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: 6, color: '#94a3b8', fontSize: 13 }}>Priority</label>
                  <input type="text" value={formData.priority} onChange={e => setFormData({...formData, priority: e.target.value})} placeholder="P0 (URGENT)" style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #334155', background: '#1e293b', color: '#f8fafc' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: 6, color: '#94a3b8', fontSize: 13 }}>Outlet ID</label>
                  <input type="text" value={formData.outletId} onChange={e => setFormData({...formData, outletId: e.target.value})} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #334155', background: '#1e293b', color: '#f8fafc' }} />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: 6, color: '#94a3b8', fontSize: 13 }}>Address</label>
                <textarea value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} rows={3} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #334155', background: '#1e293b', color: '#f8fafc', resize: 'vertical' }} />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: 6, color: '#94a3b8', fontSize: 13 }}>Google Map Link</label>
                <input type="url" value={formData.mapLocation} onChange={e => setFormData({...formData, mapLocation: e.target.value})} placeholder="https://maps.app.goo.gl/..." style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #334155', background: '#1e293b', color: '#f8fafc' }} />
              </div>

              <div style={{ display: 'flex', gap: 16 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: 6, color: '#94a3b8', fontSize: 13 }}>Location (District/Area)</label>
                  <input type="text" value={formData.location} onChange={e => setFormData({...formData, location: e.target.value})} placeholder="PATNA" style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #334155', background: '#1e293b', color: '#f8fafc' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: 6, color: '#94a3b8', fontSize: 13 }}>City</label>
                  <input type="text" value={formData.city} onChange={e => setFormData({...formData, city: e.target.value})} placeholder="PATNA" style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #334155', background: '#1e293b', color: '#f8fafc' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: 6, color: '#94a3b8', fontSize: 13 }}>State</label>
                  <input type="text" value={formData.state} onChange={e => setFormData({...formData, state: e.target.value})} placeholder="BIHAR" style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #334155', background: '#1e293b', color: '#f8fafc' }} />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: 6, color: '#94a3b8', fontSize: 13 }}>Store Manager Contact</label>
                <input type="text" value={formData.storeManagerContact} onChange={e => setFormData({...formData, storeManagerContact: e.target.value, clusterPhone: e.target.value})} placeholder="Store Manager Contact Number" style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #334155', background: '#1e293b', color: '#f8fafc' }} />
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginTop: 10, padding: '12px', background: 'rgba(255,255,255,0.03)', borderRadius: 8 }}>
                <input type="checkbox" checked={formData.active} onChange={e => setFormData({...formData, active: e.target.checked})} style={{ width: 18, height: 18 }} />
                <span style={{ color: '#f8fafc', fontWeight: 500 }}>Location is Active</span>
              </label>

              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 10 }}>
                <button type="button" onClick={() => setIsModalOpen(false)} style={{ padding: '12px 24px', borderRadius: 8, border: 'none', background: 'transparent', color: '#94a3b8', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                <button type="submit" disabled={saving} style={{ padding: '12px 24px', borderRadius: 8, border: 'none', background: '#3b82f6', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>{saving ? 'Saving...' : 'Save Location'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
